import AccountLayout from '../../components/layout/account-layout'
import IconBox from '../../components/common/icon-box'
import { UserAnimation, UserPricesDTO } from '@ringo/shared'
import { SubmitHandler, useForm } from 'react-hook-form'
import Button from '../../components/common/button'
import { toast } from 'react-toastify'
import { useTranslation } from '../../useTranslation'
import { useQueryWithLoader } from '../../components/loader/useQueryWithLoader'
import { useMutation } from '@tanstack/react-query'
import ControlledToggle from '../../components/form/controlled-toggle'
import ControlledInputNumber from '../../components/form/controlled-input-number'
import { yupResolver } from '@hookform/resolvers/yup'
import { object, boolean, number } from 'yup'
import { Validation } from '../../common/types'
import { useUser } from '../../common/hooks/auth/useUser'
import Loader from '../../components/common/Loader'
import { useUserApi } from '../../common/api-context'
import { useFormatting } from '../../common/hooks/useFormatting'

interface AccountConfiguratorData {
    displayPrices: boolean
    priceFactor: number
    animation: boolean
}

const AccountConfigurator = () => {
    const { user, refreshUserData } = useUser()
    const userApi = useUserApi()
    const { formatNumber } = useFormatting()
    const validationObject: Validation<AccountConfiguratorData> = {
        displayPrices: boolean().required(),
        priceFactor: number().min(1).max(5).required(),
    }

    const schema = object(validationObject)

    const { handleSubmit, reset, control } = useForm<AccountConfiguratorData>({
        resolver: yupResolver(schema as any),
        defaultValues: {
            animation: user?.animation,
        },
    })

    const { t } = useTranslation()

    const getUserPricesQuery = useQueryWithLoader(
        'getUserPrices',
        ['getUserPrices'],
        async () => {
            const result = (await userApi.getUserPrices()).data
            reset(result)
            return result
        },
        { refetchOnWindowFocus: false }
    )

    const updatePriceFactorMutation = useMutation({
        mutationFn: (formData: UserPricesDTO) => {
            return userApi.updateUserPrices(formData)
        },
        onSuccess: () => {
            toast.success(t('myAccount.configurator.priceFactorSuccessUpdate'))
            refreshUserData()
        },
        onError: () => {
            toast.error(t('myAccount.configurator.errorUpdatingPriceFactor'))
        },
    })

    const updateAnimationMutation = useMutation({
        mutationFn: (animation: boolean) => {
            return userApi.setAnimation({
                animation: animation,
            })
        },
        onSuccess: () => {
            refreshUserData()
            toast.success(t('myAccount.configurator.animationSuccessUpdate'))
        },
        onError: () => {
            toast.error(t('myAccount.configurator.errorUpdatingAnimation'))
        },
    })

    const updatePriceFactor: SubmitHandler<UserPricesDTO> = (data) => {
        !updatePriceFactorMutation.isPending && updatePriceFactorMutation.mutate(data as UserPricesDTO)
    }

    const updateAnimation: SubmitHandler<UserAnimation> = (data) => {
        !updateAnimationMutation.isPending && updateAnimationMutation.mutate(data.animation as boolean)
    }

    return (
        <AccountLayout>
            <div className="w-3/5 max-tablet:w-9/12">
                <IconBox icon={'money-bills'}>
                    {getUserPricesQuery.isPending || getUserPricesQuery.isFetching ? (
                        <div className="mx-auto text-center pt-[50px] pb-[30px]">
                            <Loader />
                        </div>
                    ) : (
                        <form className="pt-12 pb-6 px-2 max-tablet:pt-10 text-left text-base" onSubmit={handleSubmit(updatePriceFactor)}>
                            <h2 className="text-xl font-bold leading-none max-tablet:text-base">{t('myAccount.configurator.priceFactor')}</h2>
                            <p className="mt-1.5 mb-6 text-base max-tablet:text-sm max-tablet:mt-1">{t('myAccount.configurator.priceFactorDesc')}</p>
                            <ControlledToggle
                                label={t('myAccount.configurator.showPrice')}
                                description={t('myAccount.configurator.priceFactorActivationDesc')}
                                name="displayPrices"
                                control={control}
                            />
                            <div className="mt-6 w-1/3">
                                <ControlledInputNumber label={t('myAccount.configurator.priceFactor')} control={control} name="priceFactor" step="0.01" />
                            </div>
                            <p className="text-gray-400 text-sm italic pt-3">{`${t('example')}: 100€ * ${formatNumber(1.3)} = 130€`}</p>

                            <div className="mt-8 text-center">
                                <Button type="squareBlue" icon="check" text={t('updateChanges')} isLoading={updatePriceFactorMutation.isPending} />
                            </div>
                        </form>
                    )}
                </IconBox>

                <div className="mt-10">
                    <IconBox icon={'arrow-pointer'}>
                        <form className="pt-12 pb-6 px-2 max-tablet:pt-10 text-left text-base" onSubmit={handleSubmit(updateAnimation)}>
                            <h2 className="text-xl font-bold leading-none max-tablet:text-base">{t('myAccount.configurator.animation')}</h2>
                            <p className="mt-1.5 mb-6 text-base max-tablet:text-sm max-tablet:mt-1">{t('myAccount.configurator.animationDesc')}</p>
                            <ControlledToggle
                                label={t('myAccount.configurator.ringRotation')}
                                description={t('myAccount.configurator.animationActivationDesc')}
                                name="animation"
                                control={control}
                            />
                            <div className="mt-8 text-center">
                                <Button type="squareBlue" icon="check" text={t('updateChanges')} isLoading={updateAnimationMutation.isPending} />
                            </div>
                        </form>
                    </IconBox>
                </div>
            </div>
        </AccountLayout>
    )
}

export default AccountConfigurator
